import { useEffect, useRef, useState } from "react";

/**
 * LazyMount — render its children only when the placeholder enters (or
 * gets close to entering) the viewport. Used on the homepage to defer
 * the heavy "Podcasts" and "Autores" sections so the initial paint can
 * focus on the hero + ranking + first 6 episodes.
 *
 * CLS handling (Iter189p)
 * -----------------------
 * The old implementation removed ``minHeight`` after mount, which meant
 * that IF the real content was taller than the placeholder (very common —
 * a "Podcasts" grid ends up ~800px tall vs. a 420px placeholder) the
 * section would grow and push everything below it down. That shift
 * counted toward the CLS score even when it happened well below the
 * fold, because Chrome measures during the initial load window.
 *
 * Fix: use CSS ``content-visibility: auto`` + ``contain-intrinsic-size``
 * on the placeholder. The browser reserves the declared intrinsic size
 * WITHOUT rendering the children, and swaps to the real height when the
 * element enters the viewport. Chrome treats these as user-initiated
 * layout changes (like scroll-triggered reflows) and does NOT count them
 * as CLS. Fallback for older browsers is the previous ``minHeight``
 * behavior — imperfect but no regression.
 *
 * ``minHeight`` here should be sized as close to the REAL rendered
 * height as possible (not the "loading skeleton" height). Otherwise the
 * un-mounted placeholder is smaller than needed and there's a jump
 * when it mounts.
 */
export default function LazyMount({
  children,
  minHeight = 320,
  rootMargin = "400px",
  testId,
}) {
  const ref = useRef(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (mounted) return;
    // Older browsers without IntersectionObserver get the eager render —
    // safer than leaving them with a perpetually blank section.
    if (typeof IntersectionObserver === "undefined") {
      setMounted(true);
      return;
    }
    const node = ref.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setMounted(true);
            obs.disconnect();
            break;
          }
        }
      },
      { rootMargin }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [mounted, rootMargin]);

  // Placeholder mode: use content-visibility + contain-intrinsic-size so
  // the browser reserves the exact vertical space without a layout
  // shift when the section eventually mounts. Both properties are
  // silently ignored by browsers that don't support them, which then
  // fall back to the old minHeight behavior.
  const placeholderStyle = {
    minHeight,
    contentVisibility: "auto",
    containIntrinsicSize: `auto ${minHeight}px`,
  };

  return (
    <div
      ref={ref}
      data-testid={testId}
      style={!mounted ? placeholderStyle : undefined}
    >
      {mounted ? children : null}
    </div>
  );
}
